// DateFilter.js
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import { LimitedDatePicker, getTimeOptions, formatAmPm } from './LimitedDateTimePickers';

function DateFilter({
  date,
  time,
  onDateChange,
  onTimeChange,
  onFilter,
  filterActive,
  onReset,
}) {
  const [anchorEl, setAnchorEl] = useState(null);

  // Filter time options based on selected date to exclude past times for today
  const timeOptions = getTimeOptions(date);

  const handleButtonClick = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  const handleApply = () => {
    onFilter();
    handleMenuClose();
  };

  return (
    <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: '8px' }}>
      {/* Dropdown trigger */}
      <Button
        variant="outlined"
        onClick={handleButtonClick}
        aria-controls={anchorEl ? 'date-filter-menu' : undefined}
        aria-haspopup="true"
      >
        {filterActive ? 'Filtered' : 'Filter by Date/Time Availability'}
      </Button>

      {/* Dropdown menu */}
      <Menu
        id="date-filter-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          style: {
            padding: '8px 16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
          },
        }}
      >
        <MenuItem disableRipple disableTouchRipple>
          <LimitedDatePicker value={date} onChange={onDateChange} />
        </MenuItem>
        <MenuItem disableRipple disableTouchRipple>
          <FormControl fullWidth>
            <InputLabel id="filter-time-label">Time</InputLabel>
            <Select
              labelId="filter-time-label"
              value={time || ''}
              label="Time"
              onChange={(e) => onTimeChange(e.target.value)}
              MenuProps={{
                PaperProps: { style: { maxHeight: 175, overflowY: 'auto' } },
              }}
              IconComponent={() => null}
            >
              {timeOptions.map((t) => (
                <MenuItem key={t} value={t}>
                  {formatAmPm(t)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </MenuItem>
        <MenuItem>
          <Button fullWidth variant="contained" onClick={handleApply}>
            Apply Filter
          </Button>
        </MenuItem>
      </Menu>

      {/* External reset button */}
      {filterActive && (
        <Button variant="text" onClick={onReset}>
          Reset
        </Button>
      )}
    </div>
  );
}

DateFilter.propTypes = {
  date: PropTypes.string.isRequired,
  time: PropTypes.string.isRequired,
  onDateChange: PropTypes.func.isRequired,
  onTimeChange: PropTypes.func.isRequired,
  onFilter: PropTypes.func.isRequired,
  filterActive: PropTypes.bool,
  onReset: PropTypes.func,
};

DateFilter.defaultProps = {
  filterActive: false,
  onReset: () => {},
};

export default DateFilter;
